import type { FC, ReactNode } from 'react'
import type { Product } from '@/types'

import Link from 'next/link'
import { useRef } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { gsap, useGSAP } from '@/lib/gsap'
import { pad } from '@/lib/format'
import { prefersReducedMotion } from '@/lib/motion'
import { cn } from '@/lib/utils'
import { SplitChars } from '@/components/motion/split-text'
import Img from '@/components/generals/img'

interface TheDropProps {
    products: Array<Product>
}

const TheDrop: FC<TheDropProps> = ({ products = [] }): ReactNode => {
    const section = useRef<HTMLElement>(null)
    const track = useRef<HTMLDivElement>(null)
    const bar = useRef<HTMLSpanElement>(null)
    const counter = useRef<HTMLSpanElement>(null)

    useGSAP(() => {
        const sectionEl = section.current
        const trackEl = track.current

        if (!sectionEl || !trackEl || prefersReducedMotion()) return

        const media = gsap.matchMedia()

        media.add('(min-width: 768px)', () => {
            const distance = (): number => Math.max(0, trackEl.scrollWidth - window.innerWidth)

            const fit = (): void => {
                sectionEl.style.height = `${distance() + window.innerHeight}px`
            }

            fit()

            const frames = gsap.utils.toArray<HTMLElement>('[data-drop-photo]', trackEl)

            const scroll = gsap.to(trackEl, {
                x: () => -distance(),
                ease: 'none',
                scrollTrigger: {
                    trigger: sectionEl,
                    start: 'top top',
                    end: () => `+=${distance()}`,
                    scrub: 0.9,
                    invalidateOnRefresh: true,
                    onRefreshInit: fit,
                    onUpdate: self => {
                        if (bar.current) bar.current.style.transform = `scaleX(${self.progress})`

                        if (counter.current && products.length) {
                            const current = Math.min(products.length, Math.max(1, Math.round(self.progress * (products.length - 1)) + 1))

                            counter.current.textContent = `${pad(current)} / ${pad(products.length)}`
                        }
                    }
                }
            })

            frames.forEach(frame => {
                gsap.fromTo(frame, { xPercent: -8 }, {
                    xPercent: 8,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: frame,
                        containerAnimation: scroll,
                        start: 'left right',
                        end: 'right left',
                        scrub: true
                    }
                })
            })

            return () => {
                sectionEl.style.height = ''
            }
        })
    }, { scope: section, dependencies: [products.length] })

    if (!products.length) return null

    return (
        <section id='the-drop' ref={section} aria-label='The drop' className='relative bg-ink'>
            <div className='flex flex-col justify-center overflow-hidden py-20 md:sticky md:top-0 md:h-[100svh] md:py-0'>
                <div className='flex items-center justify-between gap-6 gutter md:pt-[var(--header-height)]'>
                    <p className='label-mono text-acid'>
                        (02) — The Drop
                    </p>

                    <p className='label-mono text-ash'>
                        {pad(products.length)} pieces
                    </p>
                </div>

                <div
                    ref={track}
                    className='no-scrollbar mt-8 flex snap-x snap-mandatory gap-5 overflow-x-auto gutter will-change-transform md:mt-10 md:gap-8 md:overflow-visible md:snap-none'
                >
                    <div className='flex w-[78vw] shrink-0 snap-start flex-col justify-end pb-2 sm:w-[52vw] md:w-[40vw]'>
                        <h2 data-reveal='chars' className='font-heading text-[clamp(44px,12.5vw,88px)] uppercase leading-[0.9] md:text-[clamp(56px,6vw,116px)]'>
                            <span className='block'>
                                <SplitChars text='Essentials' />
                            </span>

                            <span className='block text-acid'>
                                <SplitChars text='04' />
                            </span>
                        </h2>

                        <p data-reveal='fade' className='mt-6 max-w-[32ch] body-copy text-bone/70'>
                            Six pieces from the current run. Scroll sideways through the drop, each cut once in a numbered edition of 300.
                        </p>
                    </div>

                    {products.map((product, index) => (
                        <article
                            key={product.id}
                            className={cn(
                                'group w-[72vw] shrink-0 snap-start sm:w-[44vw] md:w-[min(26vw,36svh)]',
                                index % 2 === 1 && 'md:mt-12'
                            )}
                        >
                            <Link
                                href={`/shop/${product.slug}`}
                                data-cursor='View'
                                data-transition-label={product.name}
                                className='block'
                            >
                                <div className='relative aspect-[3/4] overflow-hidden bg-coal'>
                                    <div data-drop-photo className='absolute inset-[-8%]'>
                                        <Img
                                            src={product.image}
                                            alt={product.image_alt || product.name}
                                            sizes='(min-width: 768px) 30vw, 72vw'
                                            className='transition-transform duration-[1.4s] ease-expo-out group-hover:scale-105'
                                        />

                                        <Img
                                            src={product.detail_image}
                                            alt={product.detail_image_alt || product.name}
                                            sizes='(min-width: 768px) 30vw, 72vw'
                                            className='opacity-0 transition-opacity duration-700 group-hover:opacity-100'
                                        />
                                    </div>

                                    <span className='absolute left-3 top-3 bg-ink/80 px-2 py-1 meta-mono text-bone'>
                                        {pad(index + 1)}
                                    </span>

                                    <span className='absolute inset-x-3 bottom-3 flex translate-y-[calc(100%+12px)] items-center justify-between bg-bone px-3 py-2.5 label-mono text-ink transition-transform duration-700 ease-expo-out group-hover:translate-y-0'>
                                        View piece

                                        <ArrowUpRight aria-hidden='true' className='size-4' />
                                    </span>
                                </div>

                                <div className='mt-4 flex items-start justify-between gap-4'>
                                    <div>
                                        <h3 className='title-sm text-bone'>
                                            <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                                        </h3>

                                        <p className='mt-1 meta-mono text-ash'>
                                            <span data-mdlfy-cms={`products:${product.id}:category`}>{product.category}</span>
                                            {product.material ? ' — ' : null}
                                            {product.material ? <span data-mdlfy-cms={`products:${product.id}:material`}>{product.material}</span> : null}
                                        </p>
                                    </div>

                                    <p className='shrink-0 font-mono text-sm text-bone'>
                                        <span data-mdlfy-cms={`products:${product.id}:price`}>{product.price}</span>
                                    </p>
                                </div>
                            </Link>
                        </article>
                    ))}

                    <div className='flex w-[64vw] shrink-0 snap-start items-center justify-center sm:w-[36vw] md:w-[24vw]'>
                        <Link
                            href='/shop'
                            data-cursor='Shop'
                            className='group flex aspect-square w-full max-w-[280px] flex-col items-center justify-center gap-3 rounded-full border border-line text-center transition-colors duration-700 ease-expo-out hover:border-acid hover:bg-acid hover:text-ink'
                        >
                            <span className='font-heading text-[clamp(32px,3vw,48px)] uppercase leading-[0.9]'>
                                All
                                <br />
                                pieces
                            </span>

                            <ArrowUpRight aria-hidden='true' className='size-6 transition-transform duration-700 ease-expo-out group-hover:rotate-45' />
                        </Link>
                    </div>
                </div>

                <div className='mt-10 hidden items-center gap-6 gutter md:flex'>
                    <span className='relative block h-px flex-1 bg-line'>
                        <span ref={bar} className='absolute inset-0 origin-left scale-x-0 bg-acid' />
                    </span>

                    <span ref={counter} className='meta-mono tabular-nums text-ash'>
                        01 / {pad(products.length)}
                    </span>
                </div>
            </div>
        </section>
    )
}

export default TheDrop
